<script setup lang="ts">
    import Icon from '@shared/components/ui/icon/icon.vue';
    import { DateTime } from '@shared/helpers';
    import { type ComputedRef, type Ref } from 'vue';

    const props = withDefaults(defineProps<{
        modelValue: string,
        offsetDays?: number,
        allowUnselect?: boolean
    }>(), { offsetDays: 0 });

    const emit = defineEmits<{
        'update:modelValue': [value: string | null],
        'change': []
    }>();

    const CURRENT_DATE = DateTime.dayjsObject()
        .add(props.offsetDays, 'days');

    // Refs
    const calendarModel: Ref<string> = ref(props.modelValue ?? '');

    // Open the calendar on the selected date's month when prefilled, otherwise on the current month.
    const initialViewDate = calendarModel.value ? DateTime.dayjsObject(calendarModel.value) : CURRENT_DATE;
    const currentMonthIndex = ref(initialViewDate.month());
    const currentYear = ref(initialViewDate.year());

    // Computed
    const daysInMonth: ComputedRef<number[]> = computed(() => {
        const days: number[] = [];
        const daysCount = DateTime.dayjsObject()
            .year(currentYear.value)
            .month(currentMonthIndex.value)
            .daysInMonth();
        for (let i = 1; i <= daysCount; i++) {
            days.push(i);
        }
        return days;
    });

    const emptyDays: ComputedRef<string[]> = computed(() => {
        const firstDayOfMonth = DateTime.dayjsObject()
            .year(currentYear.value)
            .month(currentMonthIndex.value)
            .startOf('month')
            .day();
        return Array.from({ length: firstDayOfMonth }, () => '');
    });

    const currentMonth: ComputedRef<string> = computed(() => DateTime.dayjsObject()
        .year(currentYear.value)
        .month(currentMonthIndex.value)
        .format('MMMM'));

    const isDateDisabled: ComputedRef<(date: number) => boolean> = computed(() => (date: number) => {
        const dateToCheck = DateTime.dayjsObject()
            .year(currentYear.value)
            .month(currentMonthIndex.value)
            .date(date);
        return dateToCheck.isBefore(CURRENT_DATE, 'day');
    });

    // Methods
    const dateToString = (date: number):string => DateTime.dayjsObject()
        .year(currentYear.value)
        .month(currentMonthIndex.value)
        .date(date)
        .format('YYYY-MM-DD');

    const selectDate = async(date: number): Promise<void> => {
        const formattedDate = dateToString(date);

        // When unselect is enabled, clicking the already-selected day clears the model.
        calendarModel.value = props.allowUnselect && calendarModel.value === formattedDate
            ? ''
            : formattedDate;
        await nextTick();
        emit('change');
    };

    const nextMonth = ():void => {
        const next = DateTime.dayjsObject()
            .year(currentYear.value)
            .month(currentMonthIndex.value)
            .add(1, 'month');
        currentMonthIndex.value = next.month();
        currentYear.value = next.year();
    };

    const prevMonth = ():void => {
        const prev = DateTime.dayjsObject()
            .year(currentYear.value)
            .month(currentMonthIndex.value)
            .subtract(1, 'month');
        currentMonthIndex.value = prev.month();
        currentYear.value = prev.year();
    };

    watch(() => calendarModel.value, (nVal: string) => {
        emit('update:modelValue', nVal);
    });

    // Keep the internal state in sync when the model is set from outside (e.g. prefilled from the filter).
    watch(() => props.modelValue, (nVal: string) => {
        if (nVal === calendarModel.value) return;
        calendarModel.value = nVal ?? '';
        if (nVal) {
            const parsedDate = DateTime.dayjsObject(nVal);
            currentMonthIndex.value = parsedDate.month();
            currentYear.value = parsedDate.year();
        }
    });
</script>

<template>
    <div class="calendar">
        <div class="calendar__header">
            <h2 class="calendar__title">
                {{ currentMonth }} {{ currentYear }}
            </h2>
            <div class="calendar__actions">
                <button
                    type="button"
                    class="calendar__nav-button btn btn--icon btn--normal"
                    @click="prevMonth"
                >
                    <Icon
                        name="arrowLeft"
                        size="large"
                    />
                </button>
                <button
                    type="button"
                    class="calendar__nav-button btn btn--icon btn--normal"
                    @click="nextMonth"
                >
                    <Icon
                        name="arrowRight"
                        size="large"
                    />
                </button>
            </div>
        </div>
        <div class="calendar__header mobile">
            <button
                type="button"
                class="calendar__nav-button btn btn--icon btn--thin"
                @click="prevMonth"
            >
                <Icon
                    name="arrowLeft"
                    size="medium"
                />
            </button>
            <h2 class="calendar__title">
                {{ currentMonth }} {{ currentYear }}
            </h2>
            <button
                type="button"
                class="calendar__nav-button btn btn--icon btn--thin"
                @click="nextMonth"
            >
                <Icon
                    name="arrowRight"
                    size="medium"
                />
            </button>
        </div>
        <div class="calendar__grid">
            <div
                v-for="(_, index) in emptyDays"
                :key="`empty-${index}`"
                class="calendar__cell calendar__cell--empty"
            />
            <div
                v-for="date in daysInMonth"
                :key="date"
                class="calendar__cell"
                :class="{
                    'calendar__cell--selected': dateToString(date) === calendarModel,
                    'calendar__cell--disabled': isDateDisabled(date)
                }"
                @click="() => !isDateDisabled(date) && selectDate(date)"
            >
                {{ date.toString().padStart(2, '0') }}
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.calendar {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--spacing-03);
    gap: var(--spacing-05);

    @media (height <= 799px) {
        padding-block: 0;
    }

    @include min-width(md) {
        padding: 0;
        max-inline-size: 472px;
    }

    @include min-width(lg) {
        gap: var(--spacing-03);
        max-inline-size: 500px;
    }

    &__header {
        display: none;
        align-items: center;
        justify-content: space-between;
        inline-size: 100%;

        &.mobile {
            display: flex;

            @include min-width(md) {
                display: none;
            }
        }

        @include min-width(md) {
            display: flex;;
        }
    }

    &__title {
        margin: 0;
        font-size: var(--font-h1);
        text-transform: capitalize;
        white-space: nowrap;
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }

    &__nav-button {
        border: none;
        background: none;
        cursor: pointer;
        font-size: 24px;
    }

    &__grid {
        display: grid;
        gap: var(--spacing-01);
        grid-template-columns: repeat(7, 1fr);

        @include min-width(md) {
            gap: var(--spacing-02);
        }
    }

    &__cell {
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--color-white);
        border-radius: var(--border-radius-sm);
        background-color: rgb(255 255 255 / 0.40);
        block-size: calc(var(--size-8) - 2px);
        cursor: pointer;
        font-size: var(--font-regular);
        font-weight: var(--font-weight-light);
        inline-size: calc(var(--size-8) - 2px);
        transition: background-color 0.3s, border-color 0.3s;

        @include min-width(md) {
            block-size: var(--size-12);
            inline-size: var(--size-12);
        }

        &:hover:not(.calendar__cell--disabled):not(.calendar__cell--selected) {
            background-color: var(--background-secondary-highlight);
        }

        &--selected {
            border-color: var(--border-primary);
            background-color: var(--background-primary-invert);
            color: var(--text-primary-invert);
            font-weight: var(--font-weight-medium);
        }

        &--disabled {
            background-color: var(--background-disabled-1);
            color: var(--text-disabled-1);
            cursor: not-allowed;
            opacity: 0.9;
        }

        &--empty {
            visibility: hidden;
        }
    }
}
</style>
